/**
 * „Farben meiner Rubriken" – als Panel über dem Profil (v13, 13.1).
 *
 * Im Admin Center steht, was für den Betrieb gilt; welche Farbe die Rubrik
 * „Beschaffung" trägt, ist persönliche Vorliebe. Gespeichert wird am Profil,
 * damit dieselbe Person an zwei Rechnern dieselben Farben sieht.
 *
 * Kein mittiges Fenster mehr: Die Wahl gehört an die Stelle, an der man sie
 * aufruft, und soll die Maske dahinter sichtbar lassen – man wählt eine
 * Farbe, um zu sehen, wie sie wirkt.
 *
 * Im Ruhezustand sind nur acht Punkte zu sehen. Erst ein Klick auf eine Zeile
 * klappt die neun Töne auf, und immer nur für diese eine Zeile: 72 Farbpunkte
 * gleichzeitig waren der Grund, warum die erste Fassung verworfen wurde.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { tonAufloesen } from '../hilfen/rubrikton';
import type { Profil } from '../typen';
import './farben.css';

export function Farbpanel({ onSchliessen }: { onSchliessen: () => void }) {
  const { toastZeigen } = useKora();
  const meta = useMeta();
  const queryClient = useQueryClient();
  const [offeneZeile, setOffeneZeile] = useState<string | null>(null);

  const profil = useQuery({ queryKey: ['profil'], queryFn: () => api.hole<Profil>('/profil') });

  const speichern = useMutation({
    mutationFn: (farben: Record<string, string>) =>
      api.ersetze<{ rubrikfarben: Record<string, string> }>('/profil/rubrikfarben', { farben }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['profil'] });
      void queryClient.invalidateQueries({ queryKey: ['mein-tag'] });
      /* Die Listen färben ihre Kanten aus demselben Ton – sie müssen mit. */
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
      void queryClient.invalidateQueries({ queryKey: ['abwicklung'] });
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Die Farbe konnte nicht gespeichert werden.'),
  });

  /*
   * Die Auflösung Rubrik → Ton steht in hilfen/rubrikton.ts und nicht hier:
   * Die Listen zeigen dieselben Farben, und zwei Umsetzungen derselben
   * Regel laufen beim nächsten Ton auseinander. Der Wächter gegen eine
   * Kennung, die es nicht mehr gibt, steckt dort mit drin.
   */
  const gewaehlt = profil.data?.rubrikfarben ?? {};
  const tonVon = (rubrik: string) => tonAufloesen(rubrik, meta.toene, meta.rubrikTon, gewaehlt);

  const rubriken = meta.kacheln.map((k) => k.key);
  const label = (r: string) => meta.kacheln.find((k) => k.key === r)?.label ?? r;

  return (
    <div className="fp" role="dialog" aria-label="Farben meiner Rubriken">
      <div className="fp-kopf">
        <span className="fp-titel">Farben meiner Rubriken</span>
        <button type="button" className="fp-zu" onClick={onSchliessen} title="Schließen">
          ×
        </button>
      </div>
      <div className="fp-unter">Gilt nur für dich.</div>

      {!profil.data && <div className="modal-hinweis">Wird geladen …</div>}

      {profil.data && (
        <div className="fb-liste">
          {rubriken.map((r) => {
            const ton = tonVon(r);
            const offen = offeneZeile === r;
            return (
              <div className={`fb-zeile ${offen ? 'offen' : ''}`} key={r}>
                <button
                  type="button"
                  className="fb-kopf"
                  onClick={() => setOffeneZeile(offen ? null : r)}
                  aria-expanded={offen}
                >
                  <span className="fb-punkt" style={{ background: ton.akz }} />
                  <span className="fb-name">{label(r)}</span>
                  <span className="fb-ton">{ton.label}</span>
                  <span className="fb-pfeil">{offen ? '▾' : '▸'}</span>
                </button>

                {offen && (
                  <div className="fb-auswahl">
                    {meta.toene.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`fb-wahl ${t.id === ton.id ? 'gewaehlt' : ''}`}
                        title={t.label}
                        aria-label={`${label(r)}: ${t.label}`}
                        style={{ background: t.akz, boxShadow: t.id === ton.id ? `0 0 0 2px ${t.akzTxt}` : undefined }}
                        onClick={() => {
                          speichern.mutate({ ...gewaehlt, [r]: t.id });
                          setOffeneZeile(null);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="fp-fuss">
        <button
          type="button"
          className="fb-zuruecksetzen"
          onClick={() => {
            setOffeneZeile(null);
            speichern.mutate({});
          }}
          disabled={speichern.isPending}
        >
          Auf Standard zurücksetzen
        </button>
      </div>

      <div className="fp-hinweis">
        Die Farbe trägt Kante und Symbol der Rubrikzeile und die Kante der Karten in der Liste –
        sonst nichts. Jeder Ton bringt seine dunkle Textvariante mit, damit der Kontrast nicht von
        der Wahl abhängt.
      </div>
    </div>
  );
}
